import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowLeft, Share2, Heart, Lightbulb, Flame, Target, HandMetal } from 'lucide-react';
import { supabase } from '../../utils/supabase';

interface Post {
  id: string;
  title: string;
  slug: string;
  html_content: string;
  author_name: string;
  author_avatar: string | null;
  author_role: string | null;
  published_at: string;
  category_name: string;
  category_color: string;
  tags: string[];
  avg_read_time_seconds: number;
  view_count: number;
}

const reactionEmojis = [
  { type: 'heart', icon: Heart, label: 'Love it', color: '#FF9B50' },
  { type: 'lightbulb', icon: Lightbulb, label: 'Insightful', color: '#FFD23F' },
  { type: 'fire', icon: Flame, label: 'Fire', color: '#FF6B6B' },
  { type: 'clap', icon: HandMetal, label: 'Awesome', color: '#45B787' },
  { type: 'target', icon: Target, label: 'On point', color: '#2C3E50' }
];

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [userReaction, setUserReaction] = useState<string | null>(null);
  const [sessionId] = useState(() => `session_${Math.random().toString(36).substring(7)}`);

  useEffect(() => {
    if (slug) {
      fetchPost(slug);
      trackView(slug);
    }
  }, [slug]);

  async function fetchPost(slug: string) {
    try {
      const { data, error } = await supabase
        .from('blog_posts_published')
        .select('*')
        .eq('slug', slug)
        .single();

      if (error) throw error;
      if (data) setPost(data);
    } catch (error) {
      console.error('Error fetching post:', error);
    } finally {
      setLoading(false);
    }
  }

  async function trackView(slug: string) {
    try {
      // Increment view count
      await supabase.rpc('increment_post_view_count', { post_slug: slug });

      // Track analytics
      await supabase.from('blog_analytics').insert({
        post_id: post?.id,
        session_id: sessionId,
        referrer: document.referrer,
        device_type: /Mobile|Android|iPhone/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
        browser: navigator.userAgent.split(' ').pop()
      });
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  }

  async function handleReaction(reactionType: string) {
    if (!post) return;

    try {
      if (userReaction === reactionType) {
        // Remove reaction
        await supabase
          .from('blog_reactions')
          .delete()
          .eq('post_id', post.id)
          .eq('session_id', sessionId)
          .eq('reaction_type', reactionType);
        setUserReaction(null);
      } else {
        // Add reaction (or update if exists)
        await supabase
          .from('blog_reactions')
          .upsert({
            post_id: post.id,
            session_id: sessionId,
            reaction_type: reactionType
          });
        setUserReaction(reactionType);
      }
    } catch (error) {
      console.error('Error handling reaction:', error);
    }
  }

  async function handleShare() {
    const url = window.location.href;
    const title = post?.title || 'DoubleMe Blog';

    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        trackShareClick();
      } catch (error) {
        // User cancelled or error occurred
        copyToClipboard(url);
      }
    } else {
      copyToClipboard(url);
    }
  }

  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text);
    alert('Link copied to clipboard!');
    trackShareClick();
  }

  async function trackShareClick() {
    if (!post) return;
    await supabase.from('blog_analytics').insert({
      post_id: post.id,
      session_id: sessionId,
      clicked_share: true
    });
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatReadTime = (seconds: number) => {
    const minutes = Math.ceil(seconds / 60);
    return `${minutes} min read`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-pulse text-2xl font-display text-primary">Loading...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-display mb-4">Post Not Found</h1>
          <Link to="/blog" className="text-primary hover:underline">
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Reading Progress Bar */}
      <motion.div
        className="fixed top-0 left-0 h-1 bg-gradient-to-r from-primary via-secondary to-accent z-50"
        style={{ width: '0%' }}
        initial={{ width: '0%' }}
        whileInView={{ width: '100%' }}
        viewport={{ once: false }}
      />

      {/* Back Button */}
      <div className="border-b-2 border-dark py-4">
        <div className="max-w-4xl mx-auto px-4">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-primary transition-colors font-display"
          >
            <ArrowLeft size={20} />
            Back to Blog
          </Link>
        </div>
      </div>

      {/* Article Header */}
      <header className="py-12 border-b-2 border-dark">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Category Badge */}
            <div className="mb-6">
              <span
                className="inline-block px-4 py-2 rounded-full text-sm font-display border-2 border-dark"
                style={{ backgroundColor: post.category_color, color: '#fff' }}
              >
                {post.category_name}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display mb-6 leading-tight">
              {post.title}
            </h1>

            {/* Metadata */}
            <div className="flex flex-wrap items-center gap-6 text-gray-600">
              <div className="flex items-center gap-3">
                {post.author_avatar && (
                  <img
                    src={post.author_avatar}
                    alt={post.author_name}
                    className="w-10 h-10 rounded-full border-2 border-dark"
                  />
                )}
                <div>
                  <div className="font-display">{post.author_name}</div>
                  {post.author_role && (
                    <div className="text-sm text-gray-500">{post.author_role}</div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Calendar size={16} />
                {formatDate(post.published_at)}
              </div>
              <div className="flex items-center gap-2">
                <Clock size={16} />
                {formatReadTime(post.avg_read_time_seconds)}
              </div>
            </div>
          </motion.div>
        </div>
      </header>

      {/* Article Body */}
      <article className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="prose prose-lg max-w-none"
            style={{
              fontSize: 'clamp(1.0625rem, 2vw, 1.25rem)',
              lineHeight: '1.7'
            }}
            dangerouslySetInnerHTML={{ __html: post.html_content }}
          />
        </div>
      </article>

      {/* Reactions */}
      <div className="border-t-2 border-dark py-8">
        <div className="max-w-4xl mx-auto px-4">
          <p className="text-lg font-display mb-4">Was this helpful?</p>
          <div className="flex flex-wrap gap-3">
            {reactionEmojis.map(({ type, icon: Icon, label, color }) => (
              <button
                key={type}
                onClick={() => handleReaction(type)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full border-2 border-dark transition-all ${
                  userReaction === type
                    ? 'shadow-playful transform -translate-y-1'
                    : 'hover:shadow-playful hover:-translate-y-1'
                }`}
                style={{
                  backgroundColor: userReaction === type ? color : '#fff',
                  color: userReaction === type ? '#fff' : color
                }}
              >
                <Icon size={20} />
                <span className="font-display text-sm">{label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Share */}
      <div className="border-t-2 border-dark py-8">
        <div className="max-w-4xl mx-auto px-4">
          <button
            onClick={handleShare}
            className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-full border-2 border-dark shadow-retro hover:shadow-none hover:-translate-y-1 transition-all font-display"
          >
            <Share2 size={20} />
            Share this article
          </button>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-gradient-to-r from-primary/10 to-accent/10 border-t-2 border-dark py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-display mb-4">
            Ready to multiply yourself?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Create your AI double in 5 minutes. Never miss an opportunity again.
          </p>
          <a
            href="https://app.getadouble.com"
            className="inline-block bg-primary text-white px-8 py-4 rounded-full font-display text-lg border-2 border-dark shadow-retro hover:shadow-none hover:-translate-y-1 transition-all"
          >
            Start Free Trial →
          </a>
        </div>
      </div>
    </div>
  );
}
